import { ApplicationInsights, SeverityLevel } from '@microsoft/applicationinsights-web';
import { INSTRUMENTATION_KEYS } from 'src/constants/constants';

let heartbeatTimer = null;
let heartbeatUserId = null;
let currentUserId = null;
let sessionId = null;
let anonymousUserId = null;
let routeTrackingInitialized = false;

const SESSION_STORAGE_SESSION_KEY = 'appInsightsSessionId';
const SESSION_STORAGE_USER_KEY = 'appInsightsUserId';
const DEFAULT_DEV_TARGET_ENV = 'azureDEV';
const TARGET_ENV_ALIASES = {
  local: DEFAULT_DEV_TARGET_ENV,
  DEV1: DEFAULT_DEV_TARGET_ENV,
};

const getTargetEnv = () => {
  try {
    return process.env.TARGET_ENV;
  } catch (error) {
    return undefined;
  }
};

const getInstrumentationKey = () => {
  const targetEnv = getTargetEnv();
  const aliasTargetEnv = TARGET_ENV_ALIASES[targetEnv];
  const instrumentationKey = INSTRUMENTATION_KEYS[targetEnv] || INSTRUMENTATION_KEYS[aliasTargetEnv];

  if (!instrumentationKey && typeof console !== 'undefined') {
    console.warn(`Application Insights disabled: no instrumentation key configured for TARGET_ENV=${targetEnv || 'unknown'}`);
  }

  return instrumentationKey;
};

const safeCall = fn => {
  if (typeof fn !== 'function') return;

  try {
    fn();
  } catch (error) {
    // Never allow telemetry failures to affect application behavior.
  }
};

const createStorageId = prefix => `${prefix}-${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;

const getSessionStorageValue = (key, fallbackFactory) => {
  try {
    const existingValue = sessionStorage.getItem(key);
    if (existingValue) return existingValue;

    const newValue = fallbackFactory();
    sessionStorage.setItem(key, newValue);
    return newValue;
  } catch (error) {
    return fallbackFactory();
  }
};

const getSessionStorageSessionId = () => {
  if (sessionId) return sessionId;

  sessionId = getSessionStorageValue(SESSION_STORAGE_SESSION_KEY, () => createStorageId('session'));
  return sessionId;
};

const getSessionStorageUserId = () => {
  if (anonymousUserId) return anonymousUserId;

  anonymousUserId = getSessionStorageValue(SESSION_STORAGE_USER_KEY, () => createStorageId('user'));
  return anonymousUserId;
};

const applySessionStorageContext = instance => {
  if (!instance || !instance.context) return;

  if (instance.context.session) {
    instance.context.session.id = getSessionStorageSessionId();
  }

  if (instance.context.user) {
    instance.context.user.id = getSessionStorageUserId();
  }
};

const createAppInsightsInstance = () => {
  const instrumentationKey = getInstrumentationKey();

  if (!instrumentationKey) return null;

  try {
    const instance = new ApplicationInsights({
      config: {
        instrumentationKey,
        enableAutoRouteTracking: false,
        disableFetchTracking: false,
        disableAjaxTracking: false,
        enableCorsCorrelation: true,
        enableRequestHeaderTracking: true,
        enableResponseHeaderTracking: true,
        autoTrackPageVisitTime: true,
        enableUnhandledPromiseRejectionTracking: true,
        maxBatchInterval: 15000,
        disableExceptionTracking: false,
        endpointUrl: 'https://dc.services.visualstudio.com/v2/track',
        disableCookiesUsage: true,
        cookieCfg: {
          enabled: false,
        },
      },
    });

    instance.loadAppInsights();
    applySessionStorageContext(instance);

    if (typeof document !== 'undefined' && typeof window !== 'undefined') {
      window.appInsights = instance;
      instance.trackPageView({ name: document.title, uri: window.location.href });
      instance.flush();
    }

    return instance;
  } catch (error) {
    return null;
  }
};

const appInsights = createAppInsightsInstance();

const sanitizeUserId = value => String(value || '').replace(/[,;=|\s:]/g, '_');

const normalizeError = error => {
  if (error instanceof Error) return error;

  try {
    return new Error(error && error.message ? error.message : JSON.stringify(error));
  } catch (jsonError) {
    return new Error(String(error));
  }
};

const getSessionId = () => getSessionStorageSessionId();

export const initRouteTracking = history => {
  if (!appInsights || !history || !history.listen || routeTrackingInitialized) return;

  routeTrackingInitialized = true;
  history.listen(update => {
    safeCall(() => {
      const location = update && update.location ? update.location : update;
      const pathname = location && location.pathname ? location.pathname : window.location.pathname;
      const search = location && location.search ? location.search : window.location.search;
      const uri = typeof window !== 'undefined' ? window.location.href : pathname;

      appInsights.trackPageView({
        name: pathname,
        uri,
        properties: {
          routePath: pathname,
          search: search || '',
        },
      });
    });
  });
};

export const setAuthenticatedUser = (userId, accountId) => {
  if (!appInsights || !userId) return;

  const sanitizedUserId = sanitizeUserId(userId);
  const sanitizedAccountId = accountId ? sanitizeUserId(accountId) : undefined;
  currentUserId = sanitizedUserId;

  safeCall(() => {
    appInsights.setAuthenticatedUserContext(sanitizedUserId, sanitizedAccountId, false);
  });
};

export const stopConcurrentUserHeartbeat = () => {
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }

  heartbeatUserId = null;
};

export const clearAuthenticatedUser = () => {
  stopConcurrentUserHeartbeat();
  currentUserId = null;

  if (!appInsights) return;

  safeCall(() => {
    appInsights.clearAuthenticatedUserContext();
  });
};

export const trackEvent = (name, properties = {}) => {
  if (!appInsights || !name) return;

  safeCall(() => {
    appInsights.trackEvent({ name }, properties);
  });
};

export const trackMetric = (name, value, properties = {}) => {
  const numericValue = Number(value);
  if (!appInsights || !name || Number.isNaN(numericValue) || !Number.isFinite(numericValue)) return;

  safeCall(() => {
    appInsights.trackMetric({ name, average: numericValue, sampleCount: 1 }, properties);
  });
};

export const trackTrace = (message, severityLevel = SeverityLevel.Information, properties = {}) => {
  if (!appInsights || !message) return;

  safeCall(() => {
    appInsights.trackTrace({ message, severityLevel }, properties);
  });
};

export const trackException = (error, properties = {}) => {
  if (!appInsights || !error) return;

  safeCall(() => {
    appInsights.trackException({ exception: normalizeError(error), severityLevel: SeverityLevel.Error }, properties);
  });
};

export const trackWarning = (error, properties = {}) => {
  if (!appInsights || !error) return;

  safeCall(() => {
    appInsights.trackException({ exception: normalizeError(error), severityLevel: SeverityLevel.Warning }, properties);
  });
};

const trackHeartbeat = userId => {
  trackEvent('UserSessionHeartbeat', {
    userId,
    timestamp: new Date().toISOString(),
    sessionId: getSessionId(),
  });
};

export const startConcurrentUserHeartbeat = userId => {
  const normalizedUserId = sanitizeUserId(userId || currentUserId || '');
  if (!normalizedUserId) return;

  if (heartbeatTimer && heartbeatUserId === normalizedUserId) return;

  stopConcurrentUserHeartbeat();
  currentUserId = normalizedUserId;
  heartbeatUserId = normalizedUserId;
  trackHeartbeat(normalizedUserId);
  heartbeatTimer = setInterval(() => trackHeartbeat(normalizedUserId), 60000);
};

export const trackUserLogin = (userId, accountId) => {
  const normalizedUserId = sanitizeUserId(userId || currentUserId || '');
  if (!normalizedUserId) return;

  currentUserId = normalizedUserId;
  trackEvent('UserLoggedIn', {
    userId: normalizedUserId,
    accountId: accountId || '',
    timestamp: new Date().toISOString(),
  });
  trackMetric('ConcurrentUserLogin', 1, { userId: normalizedUserId });
};

export const trackUserLogout = userId => {
  const normalizedUserId = sanitizeUserId(userId || currentUserId || '');

  stopConcurrentUserHeartbeat();
  currentUserId = null;

  if (!normalizedUserId) return;

  trackEvent('UserLoggedOut', {
    userId: normalizedUserId,
    timestamp: new Date().toISOString(),
  });
  trackMetric('ConcurrentUserLogout', 1, { userId: normalizedUserId });
};

export const trackApiDependency = (method, url, duration, success, statusCode) => {
  const normalizedMethod = String(method || '').toUpperCase();

  trackMetric('ApiRequestDuration', duration, {
    method: normalizedMethod,
    url,
    statusCode: String(statusCode || 0),
    success: String(!!success),
  });
};

const trackNavigationTiming = () => {
  if (typeof window === 'undefined' || !window.performance) return;

  const navigationEntries = window.performance.getEntriesByType ? window.performance.getEntriesByType('navigation') : [];
  const navigation = navigationEntries && navigationEntries[0];

  if (navigation) {
    trackMetric('PageLoad_TTFB', navigation.responseStart);
    trackMetric('PageLoad_DomInteractive', navigation.domInteractive);
    trackMetric('PageLoad_DomContentLoaded', navigation.domContentLoadedEventEnd);
    trackMetric('PageLoad_FullLoad', navigation.loadEventEnd);
    return;
  }

  const timing = window.performance.timing;
  if (!timing) return;

  const navigationStart = timing.navigationStart;
  trackMetric('PageLoad_TTFB', timing.responseStart - navigationStart);
  trackMetric('PageLoad_DomInteractive', timing.domInteractive - navigationStart);
  trackMetric('PageLoad_DomContentLoaded', timing.domContentLoadedEventEnd - navigationStart);
  trackMetric('PageLoad_FullLoad', timing.loadEventEnd - navigationStart);
};

const trackPaintTiming = () => {
  if (typeof window === 'undefined' || !window.performance || !window.performance.getEntriesByType) return;

  window.performance.getEntriesByType('paint').forEach(entry => {
    if (!entry || typeof entry.startTime !== 'number') return;
    if (entry.name === 'first-paint') trackMetric('WebVital_FP', entry.startTime);
    if (entry.name === 'first-contentful-paint') trackMetric('WebVital_FCP', entry.startTime);
  });
};

const observePerformanceEntries = (type, callback) => {
  if (typeof PerformanceObserver === 'undefined') return;

  try {
    const observer = new PerformanceObserver(list => callback(list.getEntries(), observer));
    observer.observe({ type, buffered: true });
  } catch (error) {
    trackWarning(error, { context: 'trackWebVitals', metricType: type });
  }
};

export const trackWebVitals = () => {
  if (typeof window === 'undefined') return;

  trackNavigationTiming();
  trackPaintTiming();

  observePerformanceEntries('largest-contentful-paint', (entries, observer) => {
    const entry = entries[entries.length - 1];
    if (entry) trackMetric('WebVital_LCP', entry.startTime);
    observer.disconnect();
  });

  observePerformanceEntries('first-input', (entries, observer) => {
    const entry = entries[0];
    if (entry) trackMetric('WebVital_FID', entry.processingStart - entry.startTime);
    observer.disconnect();
  });

  var clsValue = 0;
  observePerformanceEntries('layout-shift', entries => {
    entries.forEach(entry => {
      if (!entry.hadRecentInput) clsValue += entry.value;
    });
    trackMetric('WebVital_CLS', clsValue);
  });
};

export const flush = () => {
  if (!appInsights) return;

  safeCall(() => {
    appInsights.flush();
  });
};

export { appInsights, SeverityLevel };